import "./TaskModal.scss";

import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import type { OptionType } from "components/inputs";
import { format } from "date-fns";
import React, { useCallback, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useGetFileByIdQuery } from "services/file-api";
import {
  useCreateTaskMutation,
  useDeleteTaskMutation,
  useUpdateTaskByIdMutation,
} from "services/task-api";
import type { TaskType } from "types/DataTypes";
import { SERVICE_TYPE } from "types/DataTypes";
import { useToken } from "utils/context";
import { getDateFromISOStringDatePart } from "utils/date-utils";
import { useCurrentUser } from "utils/hooks";
import { useSelector } from "utils/redux";

import KaboodleModal from "components/Modals/KaboodleModal";

import TaskForm from "./TaskForm";
import {
  applyApiFieldErrors,
  createTaskModalDefaultValues,
  toNumericSelectValue,
  toSingleSelectStringValue,
  toTaskDueDatePayload,
} from "../../utils/taskModal.helpers";
import type { TaskFormData } from "../../types/taskModal.types";
import { useTaskForm } from "../../hooks/useTaskForm";
import { getTaskDestinationUrl } from "../../utils/task-url-utils";
import {
  GENERIC_TASK_TYPE_CODE,
  getOpsModalTaskTypeOptions,
} from "../../utils/task-type-utils";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  fileId?: string;
  serviceId?: number;
  isLien?: boolean;
};

/* manage create/edit task flows and delegate shared form rendering. */
const TaskModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  fileId,
  serviceId,
  isLien = false,
}) => {
  const { accessToken } = useToken();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const constants = useSelector((state) => state.shared.constants);
  const currentTask = useSelector((state) => state.files.currentTask);
  const lienServices = useSelector((state) => state.liens.liens);
  const fmsService = useSelector((state) => state.futureMedical.currentService);

  const currentService = isLien ? lienServices[0] : fmsService;

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId || !accessToken || !currentUser?.has_operations_authority },
  );

  const formMethods = useTaskForm({
    defaultValues: createTaskModalDefaultValues(),
  });

  const {
    control,
    reset,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, dirtyFields },
  } = formMethods;

  const isEditMode = !!currentTask?.id;

  const services = useMemo(() => {
    if (fileData?.services) {
      return fileData.services;
    }

    if (!fileId && currentService?.file?.services) {
      return currentService.file.services;
    }

    return [];
  }, [fileData, currentService, fileId]);

  const isOpsTaskTypeSelectorVisible = !!currentUser?.has_operations_authority;

  const opsTaskTypeOptions = useMemo(() => {
    return getOpsModalTaskTypeOptions(constants?.TaskType);
  }, [constants]);

  const lienService = useMemo(
    () => services.find((option) => option.type === SERVICE_TYPE.LIEN),
    [services],
  );

  useEffect(() => {
    if (currentTask?.id) {
      reset({
        ...createTaskModalDefaultValues(),
        message: currentTask.message ?? "",
        assignee: currentTask.assignee.id,
        type: currentTask.type,
        due_date: currentTask?.due_date
          ? getDateFromISOStringDatePart(currentTask.due_date)
          : "",
        service: currentTask?.service?.id ?? null,
      });
    }
  }, [currentTask, reset]);

  useEffect(() => {
    if (serviceId) {
      setValue("service", { value: serviceId } as OptionType);
    }
  }, [serviceId, setValue]);

  useEffect(() => {
    if (isOpen && isOpsTaskTypeSelectorVisible && !currentTask?.id) {
      setValue("type", GENERIC_TASK_TYPE_CODE);
    }
  }, [isOpen, isOpsTaskTypeSelectorVisible, currentTask, setValue]);

  const [createTask, { error: createError, isSuccess: isCreateSuccess }] =
    useCreateTaskMutation();
  const [updateTaskById, { error: updateError }] = useUpdateTaskByIdMutation();
  const [deleteTask, { isSuccess: isDeleteSuccess }] = useDeleteTaskMutation();

  useEffect(() => {
    applyApiFieldErrors<TaskFormData>({
      error: createError || updateError,
      setError,
    });
  }, [createError, updateError, setError]);

  useEffect(() => {
    if (isCreateSuccess || isDeleteSuccess) {
      reset(createTaskModalDefaultValues());
      onClose();
    }
  }, [isCreateSuccess, isDeleteSuccess, onClose, reset]);

  const getService = useCallback(
    (value: TaskFormData["service"]): OptionType | undefined => {
      if (isLien && currentService) {
        return lienService;
      }

      if (isEditMode && dirtyFields?.service && !value) {
        return undefined;
      }

      if (value && services.length) {
        const selectedServiceId = toNumericSelectValue(value);

        if (typeof selectedServiceId === "number") {
          const selectedService = services.find(
            (option) => option.value === selectedServiceId,
          );

          if (selectedService) {
            return selectedService;
          }
        }
      }

      if (currentTask) {
        return {
          label: currentTask.service_type_verbose,
          value: currentTask.service?.id,
        };
      }

      if (currentService && "service" in currentService) {
        const currentServiceValue = currentService.service;
        let currentServiceId: number | undefined;

        if (typeof currentServiceValue === "number") {
          currentServiceId = currentServiceValue;
        } else if (
          currentServiceValue &&
          typeof currentServiceValue === "object" &&
          "id" in currentServiceValue &&
          typeof currentServiceValue.id === "number"
        ) {
          currentServiceId = currentServiceValue.id;
        }

        if (typeof currentServiceId === "number" && services.length > 0) {
          const matchingService = services.find(
            (option) => option.value === currentServiceId,
          );
          if (matchingService) {
            return matchingService;
          }
        }

        return {
          value: currentServiceId,
          label: currentService.type,
        };
      }

      return undefined;
    },
    [
      currentTask,
      services,
      currentService,
      isLien,
      lienService,
      isEditMode,
      dirtyFields?.service,
    ],
  );

  const onSubmit = async (data: TaskFormData) => {
    if (isEditMode && currentTask?.id) {
      const updateData: Partial<TaskType> & { due_date?: string | null } = {
        assignee: toNumericSelectValue(data.assignee),
        message: data.message,
        service: toNumericSelectValue(data.service),
        type: data.type || currentTask.type,
        due_date: toTaskDueDatePayload(data.due_date),
      };

      try {
        await updateTaskById({
          accessToken,
          id: currentTask.id,
          data: updateData,
        }).unwrap();
        KaboodleToaster.success("Task successfully updated");
        onClose();
      } catch (error) {
        console.error(error);
        const handled = applyApiFieldErrors<TaskFormData>({
          error,
          setError,
          onFieldError: KaboodleToaster.error,
        });

        if (!handled) {
          KaboodleToaster.error("Failed to update task");
        }
      }

      return;
    }

    const formattedData: TaskType = {
      ...data,
      file: null,
      type: data.type || GENERIC_TASK_TYPE_CODE,
      assignee: toNumericSelectValue(data.assignee),
      service: toNumericSelectValue(data.service),
    };

    if (fileId) {
      formattedData.file = Number(fileId);
    } else if (currentService?.file) {
      formattedData.file = currentService.file.id;
    }

    if (isLien) {
      formattedData.service = lienService?.id;
    } else if (!fileId && currentService && "service" in currentService) {
      const serviceValue = currentService.service;
      formattedData.service =
        typeof serviceValue === "object" && serviceValue !== null
          ? serviceValue.id
          : (serviceValue as number);
    }

    formattedData.due_date = toTaskDueDatePayload(data.due_date) ?? undefined;

    try {
      await createTask({ data: formattedData }).unwrap();
      KaboodleToaster.success("Task successfully created");
      onClose();
    } catch (error) {
      console.error(error);
      const handled = applyApiFieldErrors<TaskFormData>({
        error,
        setError,
        onFieldError: KaboodleToaster.error,
      });

      if (!handled) {
        KaboodleToaster.error("Failed to create task");
      }
    }
  };

  const handleClose = useCallback(() => {
    reset(createTaskModalDefaultValues());
    onClose();
  }, [onClose, reset]);

  const handleOpenService = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const detailsUrl = getTaskDestinationUrl({
        actionUrl: currentTask?.action_url,
        fileId: currentTask?.file ?? fileId ?? null,
      });

      if (!detailsUrl) {
        return;
      }

      if (event.ctrlKey || event.metaKey) {
        window.open(detailsUrl);
      } else {
        navigate(detailsUrl);
      }

      onClose();
    },
    [currentTask, fileId, navigate, onClose],
  );

  const handleDeleteTask = useCallback(async () => {
    if (currentTask?.id && accessToken) {
      try {
        await deleteTask({
          accessToken,
          id: currentTask.id,
        });
        KaboodleToaster.success("Task successfully deleted");
        onClose();
      } catch (error) {
        console.error("Failed to delete task:", error);
        KaboodleToaster.error("Failed to delete task");
      }
    }
  }, [currentTask, accessToken, deleteTask, onClose]);

  const footerContent = useMemo(() => {
    if (!isEditMode) {
      return <Button onClick={handleSubmit(onSubmit)}>Create Task</Button>;
    }

    return (
      <div className="task-modal-footer-actions">
        <Button onClick={handleOpenService}>Open Details</Button>
        <div className="task-modal-footer-primary-actions">
          <Button onClick={handleSubmit(onSubmit)}>Save Task</Button>
          <Button className="delete" onClick={handleDeleteTask}>
            Delete Task
          </Button>
        </div>
      </div>
    );
  }, [isEditMode, handleSubmit, onSubmit, handleDeleteTask, handleOpenService]);

  const isCmsResponseTask = currentTask?.type === "CR";

  return (
    <KaboodleModal isOpen={isOpen} onClose={handleClose} footer={footerContent}>
      <Card heading={isEditMode ? "Edit Task" : "Create Task"}>
        {currentTask && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-1">Created</p>
            <p className="font-medium">
              {format(new Date(currentTask.created), "MM/dd/yyyy hh:mm a")}
            </p>
          </div>
        )}

        <TaskForm
          control={control}
          errors={errors}
          assigneeLabel="Team Member"
          assigneeOptions={constants?.USERS ?? []}
          showServiceSelect={!!fileId}
          serviceOptions={services}
          serviceReadOnly={!!serviceId}
          getServiceValue={getService}
          showTaskTypeSelect={isOpsTaskTypeSelectorVisible}
          taskTypeOptions={opsTaskTypeOptions}
          taskTypeRequired={isOpsTaskTypeSelectorVisible}
          getTaskTypeValue={(value) =>
            opsTaskTypeOptions.find((option) => option.value === value)
          }
          onTaskTypeChange={(option, onChange) =>
            onChange(toSingleSelectStringValue(option))
          }
          messageMode="controlled"
          messageReadOnly={isCmsResponseTask}
          messageAdvancedEdit={!isCmsResponseTask}
        />
      </Card>
    </KaboodleModal>
  );
};

export default TaskModal;
